import { Component, Prop, Ref, SimpleEvent, log } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { html, render } from "lit-html";
import { cart } from '@/theme/Cart';

import './QuantitySpinner.scss';

@Component
export class QuantitySpinner extends ThemeComponent {
    private min: number;
    private max: number;

    constructor(min: number = 1, max: number = 99, value: number = 1) {
        super();
        this.min = min;
        this.max = max;
        this.renderSpinner(min, max, value);
    }

    // @Ref() grabs a reference to one of the children in the component using the data-ref attribute
    @Ref()
    decreaseButton!: HTMLButtonElement;
    @Ref()
    increaseButton!: HTMLButtonElement;
    @Ref()
    input!: HTMLInputElement;

    // You can use standard getters/setters to implement reactive behaviour,
    // like re-rendering or firing events
    get value() {
        return +this.input.value;
    }
    set value(value: number) {
        value = Math.min(this.max, Math.max(this.min, value));
        this.input.value = String(value);

        this.render();
        this._onChange.dispatch(value);
    }

    // Custom event that can be listened to by other components or page scripts
    private _onChange = new SimpleEvent<number>();
    get onChange() {
        return this._onChange.asEvent();
    }

    renderSpinner(min:number, max:number, value:number) {


        render(html`
            <button
                data-ref="decreaseButton"
                type="button"
                class="quantity__button quantity__button--minus"
            >
                -
            </button>
            <input
                class="quantity__input"
                max="${max}"
                min="${min}"
                type="number"
                value="${value}"
                data-ref="input"
            >
            <button
                data-ref="increaseButton"
                type="button"
                class="quantity__button">
                +
            </button>
        `, this);
    }

    // init() runs when the component should perform it's setup
    init() {
        log.debug(this.$name, 'Initing');

        // You can listen to events on children like normal

        this.increaseButton.addEventListener('click', () => this.increment());
        this.decreaseButton.addEventListener('click', () => this.decrement());
        this.input.addEventListener('input', () => this.handleInput());

        // If you add any event listeners to elements outside of this component's children,
        // you must ensure that they're cleaned up if this element is destroyed
        // You could do this in destroy(), or you can use the $listen helper
        this.$listen(window, 'resize', () => {
            this.$log.info('Resized!');
        });

        // If you subscribe to an event on another component, you should ensure that you
        // unsub when you're destroyed. The $subscribe helper does this automatically.
        // this.$subscribe(otherComponent.onChange, () => {
        //     this.$log.info('Changed!');
        // });

        this.setQuantityAttr();
        this.render();
    }


    private handleInput() {
        this.value = Number(this.input.value) || 0;
    }

    private render() {
        const inputValue = this.input.valueAsNumber > 0 ? this.input.value : 0;

        this.input.value = inputValue.toString();
    }

    // Public functions that can be called by other components or page scripts

    public increment() {
        this.value++;

        if (this.value > +this.input.min) {
            this.decreaseButton.disabled = false;
        }

        if (this.value === +this.input.max) {
            this.increaseButton.disabled = true;
            return;
        }

        this.increaseButton.disabled = false;
    }

    public decrement() {
        this.value--;

        if (this.value < +this.input.max) {
            this.increaseButton.disabled = false;
        }

        if (this.value === +this.input.min) {
            this.decreaseButton.disabled = true;
            return;
        }

        this.decreaseButton.disabled = false;
    }

    private setQuantityAttr() {
        this.classList.add('quantity');
        this.dataset.ref ='quantity';
    }

}
